/**
 * Resolves the current viewer's nav-item access — roles, connected accounts,
 * tenant persona and company count — for {@link isNavItemVisible}.
 *
 * One hook so the sidebar, the module picker and global search read the same
 * inputs; the underlying queries are React Query-cached, so the extra consumers
 * cost nothing beyond a cache read.
 */
import { useMemo } from 'react';

import { useGoogleIntegration } from '../users/hooks/useGoogleIntegration';
import { useMicrosoftIntegration } from '../users/hooks/useMicrosoftIntegration';
import { useAuth } from '../auth';
import { useMyCompanies } from '../users/hooks/useCompanies';
import type { NavItemAccess } from '../utils/navItemAccess';
import { useModuleAdminCheck } from './useIsModuleAdmin';
import { usePersona } from './usePersona';

export function useNavItemAccess(): NavItemAccess {
  const { user } = useAuth();
  const isModuleAdmin = useModuleAdminCheck();
  const { data: googleStatus } = useGoogleIntegration();
  const { data: microsoftStatus } = useMicrosoftIntegration();
  const { data: persona } = usePersona();
  const { data: companies } = useMyCompanies();

  const isAdmin = user?.is_admin === true;
  const isSysadmin = user?.is_sysadmin === true;
  const isGoogleConnected = googleStatus?.data?.google_connected === true;
  const isMicrosoftConnected = microsoftStatus?.data?.connected === true;
  const sellsFreightServices = persona?.sellsFreightServices === true;
  // Undefined while the query is in flight → false, so a gated item stays
  // hidden until we know, rather than appearing and then vanishing.
  const hasMultipleCompanies = (companies?.data?.length ?? 0) > 1;

  return useMemo(
    () => ({
      isAdmin,
      isSysadmin,
      isModuleAdmin,
      isGoogleConnected,
      isMicrosoftConnected,
      sellsFreightServices,
      hasMultipleCompanies,
    }),
    [
      isAdmin,
      isSysadmin,
      isModuleAdmin,
      isGoogleConnected,
      isMicrosoftConnected,
      sellsFreightServices,
      hasMultipleCompanies,
    ],
  );
}
